import {useSyncExternalStore} from 'react'

export const ONE_COPILOT_RELAUNCH_ANNOUNCEMENT_NOTICE = 'one_copilot_relaunch_announcement'
export const ONE_COPILOT_ENVIRONMENT_STRIP_TOUR_NOTICE = 'one_copilot_environment_strip_tour'

export type OnboardingSurface = 'free-user-popover' | 'relaunch-announcement' | 'environment-strip-tour'

let activeSurface: OnboardingSurface | null = null
let claimVersion = 0
let hasClaimedSurface = false
const surfaceListeners = new Set<() => void>()
const pendingAnnouncements = new Set<symbol>()

function notifySurfaces() {
  for (const listener of surfaceListeners) listener()
}

export function getActiveOnboardingSurface() {
  return activeSurface
}

export function hasOnboardingSurfaceClaimedForPage() {
  return hasClaimedSurface
}

export function subscribeOnboardingSurface(listener: () => void) {
  surfaceListeners.add(listener)
  return () => surfaceListeners.delete(listener)
}

function releaseOnboardingSurface(id: OnboardingSurface, version: number) {
  let released = false
  return () => {
    if (released) return
    released = true
    if (activeSurface === id && claimVersion === version) {
      activeSurface = null
      notifySurfaces()
    }
  }
}

export function claimOnboardingSurface(id: OnboardingSurface): (() => void) | null {
  if (activeSurface !== null && !(id === 'free-user-popover' && activeSurface !== 'free-user-popover')) return null
  activeSurface = id
  const version = ++claimVersion
  hasClaimedSurface = true
  notifySurfaces()
  return releaseOnboardingSurface(id, version)
}

export function handoverOnboardingSurface(from: OnboardingSurface, to: OnboardingSurface): (() => void) | null {
  if (activeSurface !== from) return null
  activeSurface = to
  const version = ++claimVersion
  hasClaimedSurface = true
  notifySurfaces()
  return releaseOnboardingSurface(to, version)
}

export function useActiveOnboardingSurface() {
  return useSyncExternalStore(subscribeOnboardingSurface, getActiveOnboardingSurface, () => null)
}

export function useHasOnboardingSurfaceClaimedForPage() {
  return useSyncExternalStore(subscribeOnboardingSurface, hasOnboardingSurfaceClaimedForPage, () => false)
}

export function hasPendingOnboardingAnnouncement() {
  return pendingAnnouncements.size > 0
}

export function usePendingOnboardingAnnouncement() {
  return useSyncExternalStore(subscribeOnboardingSurface, hasPendingOnboardingAnnouncement, () => false)
}

export function registerPendingOnboardingAnnouncement() {
  const registration = Symbol()
  pendingAnnouncements.add(registration)
  notifySurfaces()
  return () => {
    if (pendingAnnouncements.delete(registration)) notifySurfaces()
  }
}

export interface EnvironmentStripTourRequest {
  focus: boolean
}

let pendingTourRequest: EnvironmentStripTourRequest | null = null
const requestListeners = new Set<() => void>()

function notifyRequests() {
  for (const listener of requestListeners) listener()
}

export function getEnvironmentStripTourRequest(): EnvironmentStripTourRequest | null {
  return pendingTourRequest
}

export function subscribeEnvironmentStripTourRequest(listener: () => void) {
  requestListeners.add(listener)
  return () => requestListeners.delete(listener)
}

export function requestEnvironmentStripTour({focus = false}: {focus?: boolean} = {}) {
  pendingTourRequest = {focus}
  notifyRequests()
}

export function consumeEnvironmentStripTourRequest() {
  const request = getEnvironmentStripTourRequest()
  if (request) clearEnvironmentStripTourRequest()
  return request
}

export function clearEnvironmentStripTourRequest() {
  if (!pendingTourRequest) return
  pendingTourRequest = null
  notifyRequests()
}

const dismissedNotices = new Set<string>()

export function markNoticeDismissedForPage(name: string) {
  dismissedNotices.add(name)
}

export function isNoticeDismissedForPage(name: string) {
  return dismissedNotices.has(name)
}

export function resetOnboardingStateForTests() {
  clearEnvironmentStripTourRequest()
  activeSurface = null
  hasClaimedSurface = false
  claimVersion++
  pendingAnnouncements.clear()
  dismissedNotices.clear()
  notifySurfaces()
}
